import React, { useState, useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import { IoIosArrowForward } from "react-icons/io";
import { IoSearchOutline } from "react-icons/io5";
import {
  HiOutlineDocumentReport,
  HiOutlineRefresh,
  HiOutlineSwitchHorizontal,
  HiOutlineShieldCheck,
  HiOutlineGift,
} from "react-icons/hi";
import "./Report.css";
import { getCurrentMonth } from "../../../services/global_redux/globale_actions/Currentmonth.action";
import { getClients } from "../../../services/global_redux/globale_actions/Client.action";
import { getUnits } from "../../../services/global_redux/globale_actions/Unit.action";
import { getUserRole } from "../../../services/global_redux/globale_actions/Role.action";
import { isMonthLockedReport, toPayrollMonthKey, isValidPayrollDate } from "./reportRegistry";

const CATEGORY_ORDER = [
  "MONTHLY INPUTS",
  "BACKUPS & UPDATES",
  "EMPLOYEE TRANSFER DETAILS",
  "BENEFITS & INPUTS",
  "COMPLIANCE",
];

const CATEGORY_THEMES = {
  "MONTHLY INPUTS": {
    accent: "#0057ff",
    surface: "#e8f0ff",
    icon: HiOutlineDocumentReport,
    description: "Payroll registers, attendance, leave, and monthly employee inputs.",
  },
  "BACKUPS & UPDATES": {
    accent: "#d97706",
    surface: "#fff4e5",
    icon: HiOutlineRefresh,
    description: "Backup snapshots and master data changes for the payroll period.",
  },
  "EMPLOYEE TRANSFER DETAILS": {
    accent: "#7c3aed",
    surface: "#f3eeff",
    icon: HiOutlineSwitchHorizontal,
    description: "Inter-branch employee transfer records.",
  },
  "BENEFITS & INPUTS": {
    accent: "#059669",
    surface: "#e6faf3",
    icon: HiOutlineGift,
    description: "Benefit-related datasets and supplemental payroll inputs.",
  },
  COMPLIANCE: {
    accent: "#0d9488",
    surface: "#e6f7f5",
    icon: HiOutlineShieldCheck,
    description: "Statutory and compliance-oriented register exports.",
  },
};

const REPORT_CATALOG = [
  {
    category: "MONTHLY INPUTS",
    permission: "Employee Master",
    id: "employee-master",
    title: "Employee Master",
    description:
      "All active employees by default. Use month to see who was on payroll or joined that month.",
  },
  {
    category: "EMPLOYEE TRANSFER DETAILS",
    permission: "Employee Master",
    id: "employee-transfer",
    title: "Employee Transfer Details",
    description:
      "Employee transfer details between branches.",
  },
  {
    category: "BACKUPS & UPDATES",
    permission: "Employee Master",
    id: "employee-master-updates",
    title: "Employee Backup & Updates",
    description:
      "All employee master updates by default. Use month to see updates in that period.",
  },
  {
    category: "MONTHLY INPUTS",
    permission: "Salary Register",
    id: "salary-register",
    title: "Salary Register",
    description: "Earnings, deductions, and net pay by employee for the month.",
  },
  {
    category: "MONTHLY INPUTS",
    permission: "CTC Master",
    id: "ctc-master",
    title: "CTC Master",
    description: "Cost-to-company structure and components by employee.",
  },
  {
    category: "MONTHLY INPUTS",
    permission: "Salary Advance",
    id: "salary-advances",
    title: "Salary Advance",
    description: "Salary advance transactions and recoveries for the month.",
  },
  {
    category: "MONTHLY INPUTS",
    permission: "Other Inputs",
    id: "other-inputs",
    title: "Other Inputs",
    description: "Additional payroll-related inputs for the active month.",
  },
  {
    category: "MONTHLY INPUTS",
    permission: "Leave Register",
    id: "leave-register",
    title: "Leave Register",
    description: "Leave transactions and balances by employee.",
  },
  {
    category: "MONTHLY INPUTS",
    permission: "Attendance Register Override",
    id: "attendance-override",
    title: "Attendance Override",
    description: "Manual attendance / leave grid overrides for the month.",
  },
  {
    category: "MONTHLY INPUTS",
    permission: "Generate Attendance Register",
    id: "attendance-register",
    title: "Attendance Register",
    description: "Generated attendance register lines for the month.",
  },
  {
    category: "MONTHLY INPUTS",
    permission: "Maternity Benefits",
    id: "maternity-benefits",
    title: "Maternity Benefits",
    description: "Maternity benefit records and entitlements by employee.",
  },
  {
    category: "MONTHLY INPUTS",
    permission: "Tamil Nadu Form VI",
    id: "tamil-nadu-form-vi",
    title: "TN Form VI",
    description: "Tamil Nadu Form VI compliance dataset for the payroll month.",
  },
  {
    category: "MONTHLY INPUTS",
    permission: "Employee Separation",
    id: "emp-separation",
    title: "Employee Separation",
    description: "All exited employees from start to now. Use month to see who exited that month.",
  },
  {
    category: "MONTHLY INPUTS",
    permission: "Staffing Agencies",
    id: "contractor",
    title: "Staffing Agencies",
    description: "Contractor and staffing agency master for the client.",
  },
  {
    category: "MONTHLY INPUTS",
    permission: "Leave Opening Balance",
    id: "leave-opening-balance",
    title: "Leave Opening Balance",
    description: "Opening leave balances by employee for the period.",
  },
  {
    category: "BACKUPS & UPDATES",
    permission: "Employee Master",
    id: "org-backup",
    title: "Organization Backup Details",
    description: "Backup and update records for organization master data and configurations.",
  },
  {
    category: "BACKUPS & UPDATES",
    permission: "Employee Master",
    id: "branch-backup",
    title: "Branch Backup Details",
    description: "Backup and update records for branch master data and related configurations.",
  },
];

function getSessionUser() {
  try {
    return JSON.parse(sessionStorage.getItem("user") || "{}") || {};
  } catch {
    return {};
  }
}

function getAssignedClientUnits() {
  try {
    const mapped = JSON.parse(getSessionUser().client_and_unit || "[]");
    return Array.isArray(mapped) ? mapped : [];
  } catch {
    return [];
  }
}

function getAssignedUnits(clientId) {
  const entry = getAssignedClientUnits().find(
    (item) => String(item.client_id) === String(clientId)
  );
  return (entry?.unit_id || []).map(String);
}

function orgOptionLabel(client) {
  if (!client) return "";
  const id = String(client.client_id || "").trim();
  const name = String(client.client_name || "").trim();
  if (id && name) return `${id} - ${name}`;
  return id || name;
}

function branchOptionLabel(unit) {
  const name = String(unit?.unit_name || "").trim();
  const location = String(unit?.Location_City || unit?.location_city || "").trim();
  const id = String(unit?.unit_id || "").trim();
  let place = "";
  if (name && location) place = `${name} (${location})`;
  else place = name || location || "";
  if (id && place) return `${id} - ${place}`;
  return id || place;
}

function toMonthFirstDay(value) {
  return toPayrollMonthKey(value);
}

const formatPayrollMonthLabel = (dateStr) => {
  if (!dateStr) return "";
  const dt = new Date(dateStr);
  if (Number.isNaN(dt.getTime())) return "";
  return dt.toLocaleString("default", { month: "long", year: "numeric" });
};

function Reports() {
  const dispatch = useDispatch();
  const sessionUser = getSessionUser();
  const isClient = sessionUser?.base_role === "Client";

  const [currentMonth, setCurrentMonth] = useState("");
  const [permissionsSubmodules, setPermissionsSubmodules] = useState(null);
  const [selectedClientId, setSelectedClientId] = useState("");
  const [selectedUnitId, setSelectedUnitId] = useState("");
  const [clientIDs, setClientIDs] = useState([]);
  const [unitOptions, setUnitOptions] = useState([]);
  const [dater, setDater] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  const loadUnitsForClient = async (clientId) => {
    if (!clientId) {
      setUnitOptions([]);
      setSelectedUnitId("");
      sessionStorage.removeItem("selectedUnit");
      return;
    }
    try {
      const assigned = getAssignedUnits(clientId);
      const response = await dispatch(getUnits(clientId));
      const rows = Array.isArray(response.data) ? response.data : [];
      const filtered = rows.filter((unit) => {
        if (String(unit.client_id) !== String(clientId)) return false;
        if (!assigned.length) return true;
        return assigned.includes(String(unit.unit_id));
      });
      setUnitOptions(filtered);
      const storedUnit = sessionStorage.getItem("selectedUnit");
      const storedMatch = filtered.find((unit) => String(unit.unit_id) === String(storedUnit));
      if (storedMatch) {
        setSelectedUnitId(String(storedMatch.unit_id));
        sessionStorage.setItem("selectedUnit", String(storedMatch.unit_id));
      } else if (filtered.length === 1) {
        const onlyId = String(filtered[0].unit_id);
        setSelectedUnitId(onlyId);
        sessionStorage.setItem("selectedUnit", onlyId);
      } else {
        setSelectedUnitId("");
        sessionStorage.removeItem("selectedUnit");
      }
    } catch (err) {
      console.error("Failed to fetch branches:", err);
      setUnitOptions([]);
      setSelectedUnitId("");
    }
  };

  const loadCurrentMonthForClient = async (clientId) => {
    if (!clientId) {
      setCurrentMonth("");
      setDater("");
      return;
    }
    sessionStorage.setItem("selectedClient", clientId);
    try {
      const response = await dispatch(getCurrentMonth(clientId));
      const row = Array.isArray(response.data) ? response.data[0] : null;
      const raw = row?.current_month || "";
      const monthKey = toMonthFirstDay(raw);
      setCurrentMonth(raw);
      setDater(monthKey);
    } catch (err) {
      console.error("Failed to fetch current month:", err);
      setCurrentMonth("");
      setDater("");
    }
  };

  const fetchAllClient = async () => {
    try {
      const response = await dispatch(getClients());
      const rows = Array.isArray(response.data) ? response.data : [];
      setClientIDs(rows);
      const storedClient = sessionStorage.getItem("selectedClient");
      const lockedClient = isClient
        ? (storedClient && storedClient !== "null" ? storedClient : getAssignedClientUnits()[0]?.client_id || "")
        : "";
      const initialClient =
        (lockedClient && rows.some((c) => String(c.client_id) === String(lockedClient)) && lockedClient) ||
        (storedClient && storedClient !== "null" && rows.some((c) => String(c.client_id) === String(storedClient))
          ? storedClient
          : "");
      if (initialClient) {
        setSelectedClientId(String(initialClient));
        sessionStorage.setItem("selectedClient", String(initialClient));
        await loadUnitsForClient(initialClient);
        await loadCurrentMonthForClient(initialClient);
      }
    } catch (err) {
      console.error("Failed to fetch clients:", err);
    }
  };

  const getUserPermission = async () => {
    try {
      const response = await dispatch(getUserRole());
      const rawPermissions = response?.data?.permissions;
      const parsedPermissions =
        typeof rawPermissions === "string"
          ? JSON.parse(rawPermissions)
          : Array.isArray(rawPermissions) && typeof rawPermissions[0] === "string"
          ? JSON.parse(rawPermissions[0])
          : rawPermissions;

      const modules = [];
      parsedPermissions?.forEach((item) => {
        const { section, module, sub_module } = item;
        if (
          (section === "InputMatrix" && module === "InputMatrix") ||
          (section === "Report" && module === "Report")
        ) {
          if (Array.isArray(sub_module)) {
            modules.push(...sub_module);
          }
        }
      });
      if (modules.length > 0) {
        setPermissionsSubmodules(modules);
      }
    } catch (err) {
      console.error("Failed to fetch permissions:", err);
    }
  };

  useEffect(() => {
    fetchAllClient();
    getUserPermission();
  }, [dispatch]);

  const permittedNames = useMemo(() => {
    if (!permissionsSubmodules?.length) return new Set();
    return new Set(permissionsSubmodules.map((m) => m.name));
  }, [permissionsSubmodules]);

  const visibleReports = useMemo(() => {
    const permissionsReady = permissionsSubmodules != null;
    return REPORT_CATALOG.filter((r) => {
      if (!permissionsReady) return true;
      if (!r.permission) return true;
      return permittedNames.has(r.permission);
    });
  }, [permittedNames, permissionsSubmodules]);

  const filteredReports = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return visibleReports;
    return visibleReports.filter(
      (r) =>
        r.title.toLowerCase().includes(q) ||
        r.id.toLowerCase().includes(q) ||
        r.category.toLowerCase().includes(q) ||
        (r.description && r.description.toLowerCase().includes(q))
    );
  }, [visibleReports, searchQuery]);

  const groupedReports = useMemo(() => {
    const map = new Map();
    filteredReports.forEach((r) => {
      if (!map.has(r.category)) map.set(r.category, []);
      map.get(r.category).push(r);
    });
    return map;
  }, [filteredReports]);

  const handleClientChange = async (id) => {
    if (isClient) return;
    setSelectedClientId(id);
    setSelectedUnitId("");
    setUnitOptions([]);
    setCurrentMonth("");
    setDater("");
    if (!id) {
      sessionStorage.removeItem("selectedClient");
      sessionStorage.removeItem("selectedUnit");
      return;
    }
    sessionStorage.setItem("selectedClient", id);
    sessionStorage.removeItem("selectedUnit");
    await loadUnitsForClient(id);
    await loadCurrentMonthForClient(id);
  };

  const handleUnitChange = (id) => {
    setSelectedUnitId(id);
    if (!id) {
      sessionStorage.removeItem("selectedUnit");
      return;
    }
    sessionStorage.setItem("selectedUnit", id);
  };

  const monthLabel = formatPayrollMonthLabel(currentMonth || dater);
  const reportsReady = Boolean(selectedClientId && selectedUnitId);
  const reportsDisabled = !reportsReady;

  return (
    <div className="reports-hub" data-tour="reports-hub">
      <div className="reports-hero-card">
        <header className="reports-header">
          <div className="reports-header-text">
            <h1>Reports</h1>
          </div>
          {monthLabel ? (
            <div className="reports-header-actions">
              <span className="reports-live-badge">Current month</span>
              <span className="reports-month-pill">{monthLabel}</span>
            </div>
          ) : null}
        </header>

        <div className="reports-scope-row">
          <label className="reports-scope-field">
            <span>Organization</span>
            <select
              className="reports-org-select"
              value={selectedClientId}
              onChange={(e) => handleClientChange(e.target.value)}
              disabled={isClient}
              aria-label="Organization"
            >
              <option value="">Select organization</option>
              {clientIDs.map((client) => (
                <option key={client.client_id} value={client.client_id}>
                  {orgOptionLabel(client)}
                </option>
              ))}
            </select>
          </label>
          <label className="reports-scope-field">
            <span>Branch</span>
            <select
              className="reports-org-select"
              value={selectedUnitId}
              onChange={(e) => handleUnitChange(e.target.value)}
              disabled={!selectedClientId}
              aria-label="Branch"
            >
              <option value="">Select branch</option>
              {unitOptions.map((unit) => (
                <option key={unit.unit_id} value={unit.unit_id}>
                  {branchOptionLabel(unit)}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="reports-toolbar">
          <div
            className={`reports-search-wrap ${reportsDisabled ? "reports-toolbar--disabled" : ""}`}
          >
            <IoSearchOutline className="reports-search-icon" aria-hidden />
            <input
              type="search"
              className="reports-search"
              placeholder="Search reports by name, code, or category..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              disabled={reportsDisabled}
            />
          </div>
          <span className="reports-available-count">
            <span className="reports-meta-chip__label">Available reports</span>
            <span className="reports-meta-chip__value">{filteredReports.length}</span>
          </span>
        </div>
      </div>

      <div className={`reports-sections ${reportsDisabled ? "reports-sections--disabled" : ""}`}>
        {CATEGORY_ORDER.map((category) => {
          const items = groupedReports.get(category);
          if (!items?.length) return null;

          const theme = CATEGORY_THEMES[category] || CATEGORY_THEMES["MONTHLY INPUTS"];
          const CategoryIcon = theme.icon;

          return (
            <section
              key={category}
              className="reports-section-card"
              style={{ "--section-accent": theme.accent }}
            >
              <div className="reports-section-header">
                <div
                  className="reports-section-header__icon"
                  style={{ background: theme.surface, color: theme.accent }}
                >
                  <CategoryIcon aria-hidden />
                </div>
                <div className="reports-section-header__text">
                  <h2>{category}</h2>
                  <p>{theme.description}</p>
                </div>
                <span className="reports-section-count">
                  {items.length} report{items.length === 1 ? "" : "s"}
                </span>
              </div>

              <div className="reports-grid">
                {items.map((report) => (
                  <ReportCard
                    key={report.id}
                    report={report}
                    link={
                      isMonthLockedReport(report.id)
                        ? `/reports/report/${report.id}/${dater}`
                        : `/reports/report/${report.id}/all`
                    }
                    disabled={
                      reportsDisabled ||
                      (isMonthLockedReport(report.id) && !isValidPayrollDate(dater))
                    }
                    theme={theme}
                  />
                ))}
              </div>
            </section>
          );
        })}

        {filteredReports.length === 0 && reportsReady && (
          <div className="reports-empty-card">
            <p className="reports-empty">No reports match your search or permissions.</p>
          </div>
        )}
      </div>
    </div>
  );
}

function ReportCard({ report, link, disabled, theme }) {
  const content = (
    <>
      <div
        className="report-card-icon"
        style={{ background: theme.surface, color: theme.accent }}
        aria-hidden
      >
        <HiOutlineDocumentReport />
      </div>
      <div className="report-card-body">
        <div className="report-card-top">
          <h3>{report.title}</h3>
          <span className="report-card-chevron">
            <IoIosArrowForward />
          </span>
        </div>
        <p className="report-card-desc">{report.description}</p>
        <span className="report-card-slug" title={`Report code: ${report.id}`}>
          {report.id}
        </span>
      </div>
    </>
  );

  if (disabled) {
    return (
      <div className="report-card report-card--disabled" aria-disabled="true">
        <div className="report-card-inner">{content}</div>
      </div>
    );
  }

  return (
    <a
      href={link}
      className="report-card text-decoration-none text-dark"
      title={`Open ${report.title}`}
    >
      <div className="report-card-inner">{content}</div>
    </a>
  );
}

export default Reports;
